import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import type { GhostTarget } from '../api/admin.ts'
import type { ApiFetch } from '../api/client.ts'
import { useAuth } from '../auth/AuthProvider.tsx'

// "Ghost mode" as the portal sees it: which user a Curri admin is currently
// reading the dashboard as.
//
// This is *view* state, not session state. The OAuth session still belongs to the
// admin — `/api/me` is still fetched without the header and still reports their
// own identity — and the only thing that changes is a header on the data reads.
// Keeping it out of `AuthProvider` is what makes that true by construction: no
// amount of ghosting can alter who is signed in.
//
// The header itself is validated server-side on every request
// (services/curri-integrations-service/src/middleware/ghost.ts): it is honored
// only for a Curri admin, only on GET/HEAD, and only for a user that exists. The
// `isCurriAdmin` gate below decides whether to render the control, not whether
// the feature is allowed — a non-admin sending the header by hand gets a 403.

export const AS_USER_HEADER = 'Curri-As-User'

// sessionStorage rather than the URL: the OAuth redirect leg rewrites the address
// bar with `history.replaceState` when it strips the authorization code, so a
// query parameter does not survive a reload that re-authorizes. Per-tab is also
// the right lifetime — two tabs viewing two different partners is a normal thing
// for a support engineer to want, and a target user id is not a credential.
const STORAGE_KEY = 'curri-portal.ghost'

const readStored = (): GhostTarget | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as GhostTarget
    // Anything else in this key is someone else's data or a stale shape; the
    // server would reject it anyway, and a bad restore is worse than none.
    return typeof parsed?.userId === 'number' ? parsed : null
  } catch {
    // Blocked storage or malformed JSON is not a reason to fail to render.
    return null
  }
}

const writeStored = (target: GhostTarget | null): void => {
  try {
    if (!target) {
      sessionStorage.removeItem(STORAGE_KEY)
      return
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target))
  } catch {
    // Best-effort: ghosting still works for this render, it just won't survive
    // a reload.
  }
}

interface GhostContextValue {
  /** Null when viewing as yourself, which is every non-admin session. */
  target: GhostTarget | null
  /** Whether this session may ghost at all — a Curri admin's. */
  isAvailable: boolean
  enter: (target: GhostTarget) => void
  exit: () => void
}

const GhostContext = createContext<GhostContextValue | null>(null)

export const GhostProvider = ({ children }: { children: ReactNode }) => {
  const { state } = useAuth()
  const isAvailable =
    state.status === 'authenticated' && state.caller.isCurriAdmin

  const [target, setTarget] = useState<GhostTarget | null>(null)
  // Distinguishes "not signed in yet" from "signed out". Both look like
  // `!isAvailable`, but only the second should discard the stored target —
  // clearing on the first would wipe it during every reload, before the session
  // has even resolved.
  const wasAvailable = useRef(false)

  useEffect(() => {
    if (isAvailable) {
      wasAvailable.current = true
      setTarget(readStored())
      return
    }

    setTarget(null)
    if (wasAvailable.current) writeStored(null)
  }, [isAvailable])

  const enter = useCallback((next: GhostTarget) => {
    setTarget(next)
    writeStored(next)
  }, [])

  const exit = useCallback(() => {
    setTarget(null)
    writeStored(null)
  }, [])

  const value = useMemo<GhostContextValue>(
    () => ({ target, isAvailable, enter, exit }),
    [target, isAvailable, enter, exit]
  )

  return <GhostContext.Provider value={value}>{children}</GhostContext.Provider>
}

export const useGhost = (): GhostContextValue => {
  const value = useContext(GhostContext)
  if (!value) {
    // A screen reading ghost state outside the provider would silently render as
    // "viewing as yourself" while the banner said otherwise.
    throw new Error('useGhost was called outside a GhostProvider')
  }
  return value
}

/**
 * The fetch every ghostable read should use, in place of `useAuth().apiFetch`.
 *
 * Its identity changes with the target, which is what makes the data hooks
 * refetch on enter and exit — they already list the fetch function in their
 * dependencies, so switching users needs no extra plumbing.
 */
export const useApiFetch = (): ApiFetch => {
  const { apiFetch } = useAuth()
  const { target } = useGhost()

  return useCallback(
    (path, init = {}) => {
      if (!target) return apiFetch(path, init)

      // Through `Headers` for the same reason the shared `apiFetch` does it:
      // `HeadersInit` may be an array or a `Headers`, and spreading either drops
      // everything the caller passed.
      const headers = new Headers(init.headers)
      headers.set(AS_USER_HEADER, String(target.userId))
      return apiFetch(path, { ...init, headers })
    },
    [apiFetch, target]
  )
}
